/**
 * Générateur — Automatisme : appliquer une augmentation ou une diminution en
 * pourcentage, avec ou sans coefficient multiplicateur.
 * Attendu ID : 1254 | Niveau : 3ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 19, lignes 1392-1393 :
 * « Appliquer, dans des cas simples, une augmentation ou une diminution
 * exprimée en pourcentages, en utilisant ou non le coefficient
 * multiplicateur. »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Deux gestes = deux cartes : la VALEUR (« 150 € augmentés de 20 % :
 * 180 € », sans passer par le coefficient) et le COEFFICIENT (« augmenter
 * de 20 %, c'est multiplier par 1,2 »), la carte de gen_219 (3e objectif)
 * resserrée aux cas simples. p ∈ {5, 10, 20, 25, 30, 50} (15 et 40 : choix
 * mode auto, non ; À ARBITRER) ; V multiple de 20, ≤ 1 000 ; hausse ou
 * baisse à parts égales, un 5 % sur trois lignes. Hors : le taux d'évolution
 * (objectif l. 1396, gen_219), la valeur initiale, les évolutions
 * successives, le complémentaire, la fonction linéaire (218).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5DC1
 * (5, 50 % puis 10, 20, 30 % ; quantités multiples de 12).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : trois lignes « 150 €
 *     augmentés de 20 % : ⬚ € » / « 80 € diminués de 25 % : ⬚ € ».
 *   Carte 2 « La nouvelle valeur » (valeur_qcm_unique) : « Un article coûte
 *     150 €. Son prix augmente de 20 %. Il coûte alors… » [180 € ; 170 € ;
 *     30 € ; 120 €].
 *   Carte 3 « Le coefficient » (coefficient_qcm_unique) : « Augmenter de
 *     20 %, c'est multiplier par… » [1,2 ; 0,2 ; 1,02 ; 0,8] ; une fois sur
 *     deux en situation : « Un article coûte 240 €. Son prix augmente de
 *     20 %. Le nouveau prix, c'est 240 × … ».
 *   Carte 4 « Vrai ou faux » (sens_tableau_vf) : quatre lignes mêlant les
 *     deux gestes (« Diminuer de 20 %, c'est multiplier par 0,8. » V).
 *
 * INVARIANTS : valeurs en CENTIÈMES entiers ; la nouvelle valeur
 * re-calculée par les deux chemins (V ± p % de V et V × coefficient,
 * throw si ≠) ; coefficients en centièmes (1,2 = 120) ; distracteurs
 * distincts.
 * SACRIFIÉ (fiche : la barre 100 % + la case « + 20 % » = 120 % = × 1,2) :
 * (150 € ; +20 %) en valeur ; le coefficient 1,2 / 0,8 n'est PAS sacrifié
 * (fait à connaître, exemption comme 1047) ; auto-testé par
 * gen1254.self.test.js.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   pourcentage_recopie   : 150 + 20 = 170 (le pourcentage ajouté tel quel), PRINCIPALE (valeur) ;
 *   pourcentage_seul      : 30 (la hausse seule, pas la nouvelle valeur) ;
 *   sens_inverse          : 120 (diminué au lieu d'augmenté) ;
 *   mauvais_diviseur      : 150 ÷ 20 pris pour 20 % ;
 *   coefficient_sans_le_1 : 0,2 pour + 20 % (PRINCIPALE, coefficient) ;
 *   coefficient_accole    : 1,5 pour + 5 % (le chiffre collé après la virgule) ;
 *   coefficient_signe     : 0,8 pour + 20 % (le sens inversé) ;
 *   coefficient_additif   : 1 + 20 = 21.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1254;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_VALEUR = 'La nouvelle valeur';
const L_COEF = 'Le coefficient';
const L_VF = 'Vrai ou faux';

// ─── Le domaine (centièmes entiers) ───────────────────────────────────────

const PCTS = [5, 10, 20, 25, 30, 50];
/** L'exemple de la fiche : 150 € + 20 % = 180 €. */
/** (150 € ; 20 %) dans les DEUX sens : la fiche montre la hausse et la baisse côte à côte (prof 2026-09-19). */
export const SACRIFIE = { V: 150, p: 20 };

/** Centièmes → « 1,2 », « 0,85 », « 180 ». */
const fmt = (c) => { const e = Math.floor(c / 100); const r = c % 100; return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`; };
/** Le coefficient en centièmes (+20 % → 120 ; −25 % → 75). */
const coefDe = (p, hausse) => (hausse ? 100 + p : 100 - p);
/** La nouvelle valeur (entière), par les deux chemins (throw). */
const nouvelle = (V, p, hausse) => {
    const v1 = hausse ? V + (V * p) / 100 : V - (V * p) / 100;
    const v2 = (V * coefDe(p, hausse)) / 100;
    if (!Number.isInteger(v1) || v1 !== v2) throw new Error(`gen_1254 : ${V} ${p} ${hausse}`);
    return v1;
};

/** Un item { V, p, hausse, coef, nv } : V multiple de 20 ≤ 1 000, p dans PCTS. */
const tirer = ({ hausse = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const p = randomChoice(PCTS);
        const h = hausse ?? randomChoice([true, false]);
        const V = 20 * randInt(1, 50);
        if (exclure.includes(`${V}|${p}|${h}`)) continue;
        if (V === SACRIFIE.V && p === SACRIFIE.p) continue;
        return { V, p, hausse: h, coef: coefDe(p, h), nv: nouvelle(V, p, h) };
    }
    return { V: 80, p: 25, hausse: false, coef: 75, nv: 60 };
};

const verbe = (h) => (h ? 'augmentés' : 'diminués');
const EXPLICATIONS = {
    pourcentage_recopie: '20 %, ce n\'est pas 20 € : c\'est 20 % de 150 €, soit 30 €. 150 + 30 = 180 €.',
    pourcentage_seul: '30 €, c\'est la hausse ; la nouvelle valeur, c\'est 150 + 30 = 180 €.',
    sens_inverse: 'Augmenter, c\'est ajouter : 150 + 30 = 180 €, pas 150 − 30.',
    mauvais_diviseur: '20 % de 150, c\'est 150 ÷ 5 (un cinquième) = 30, pas 150 ÷ 20.',
    coefficient_sans_le_1: 'Augmenter de 20 %, c\'est garder les 100 % et ajouter 20 % : 120 %, soit × 1,2. × 0,2 ne garde que 20 %.',
    coefficient_accole: '5 %, c\'est 0,05 : augmenter de 5 %, c\'est × 1,05, pas × 1,5 (ce serait + 50 %).',
    coefficient_signe: 'Augmenter, c\'est multiplier par plus que 1 (1,2) ; diminuer, par moins que 1 (0,8).',
    coefficient_additif: 'Le coefficient s\'obtient sur 100 % : (100 + 20) ÷ 100 = 1,2.',
    regle: 'Augmenter de p %, c\'est multiplier par (1 + p/100) ; diminuer de p %, c\'est multiplier par (1 − p/100). Sans coefficient : je calcule p % de la valeur, puis j\'ajoute ou je retire.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les candidats pour la VALEUR (en unités), jamais la clé ni deux fois la même valeur. */
const candidatsValeur = (it) => {
    const { V, p, hausse, nv } = it;
    const c = [];
    const pousser = (code, v) => { if (Number.isInteger(v) && v > 0 && v !== nv && v !== V && !c.some((x) => x.v === v)) c.push({ code, v }); };
    pousser('pourcentage_recopie', hausse ? V + p : V - p);
    pousser('pourcentage_seul', (V * p) / 100);
    pousser('sens_inverse', nouvelle(V, p, !hausse));
    if (V % p === 0) pousser('mauvais_diviseur', hausse ? V + V / p : V - V / p);
    return c;
};
/** Les candidats pour le COEFFICIENT (en centièmes). */
const candidatsCoef = (it) => {
    const { p, hausse, coef } = it;
    const c = [];
    const pousser = (code, v) => { if (Number.isInteger(v) && v > 0 && v !== coef && !c.some((x) => x.v === v)) c.push({ code, v }); };
    pousser('coefficient_sans_le_1', p);
    pousser('coefficient_signe', coefDe(p, !hausse));
    if (p < 10) pousser('coefficient_accole', hausse ? 100 + 10 * p : 100 - 10 * p);
    pousser('coefficient_additif', (hausse ? 100 + p : 100 - p) * 100);
    return c;
};
const trois = (cands) => {
    const pris = [];
    for (const x of shuffle(cands)) if (!pris.some((y) => y.code === x.code)) pris.push(x);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const ligneFlash = (it) => `${it.V} € ${verbe(it.hausse)} de ${it.p} % : {{${it.nv}}} €`;

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [tirer({ hausse: true }), tirer({ hausse: false }), tirer()];
        if (new Set(items.map((it) => `${it.V}|${it.p}|${it.hausse}`)).size < 3) continue;
        if (!items.some((it) => it.p === 5) && randInt(1, 3) === 1) continue; // un 5 % une fois sur trois environ
        const payload = makeTexteTrou(shuffle(items).map(ligneFlash).join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, pourcentage_recopie: EXPLICATIONS.pourcentage_recopie } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [{ V: 80, p: 25, hausse: false, coef: 75, nv: 60 }, { V: 200, p: 30, hausse: true, coef: 130, nv: 260 }, { V: 400, p: 5, hausse: true, coef: 105, nv: 420 }];
    const payload = makeTexteTrou(items.map(ligneFlash).join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : La nouvelle valeur (QCM) ───────────────────────────────────

const genValeur = () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirer();
        const faux = trois(candidatsValeur(it));
        if (!faux) continue;
        const bon = `${it.nv} €`;
        const choix = [bon, ...faux.map((f) => `${f.v} €`)];
        if (new Set(choix).size !== 4) continue;
        const stem = `<p>Un article coûte ${it.V} €. Son prix ${it.hausse ? 'augmente' : 'diminue'} de ${it.p} %.</p><p><b>Il coûte alors…</b></p>`;
        const payload = makeQcm(choix, bon, stem, opts(L_VALEUR));
        payload.contenu = { ...payload.contenu, sousType: it.hausse ? 'hausse' : 'baisse', explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = { V: 80, p: 25, hausse: false, coef: 75, nv: 60 };
    const payload = makeQcm(['60 €', '55 €', '20 €', '100 €'], '60 €', `<p>Un article coûte 80 €. Son prix diminue de 25 %.</p><p><b>Il coûte alors…</b></p>`, opts(L_VALEUR));
    payload.contenu = { ...payload.contenu, sousType: 'baisse', explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'pourcentage_recopie' }, { code: 'pourcentage_seul' }, { code: 'sens_inverse' }] };
    return payload;
};

// ─── Carte 3 : Le coefficient (QCM) ───────────────────────────────────────

const genCoef = () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirer();
        const faux = trois(candidatsCoef(it));
        if (!faux) continue;
        const bon = fmt(it.coef);
        const choix = [bon, ...faux.map((f) => fmt(f.v))];
        if (new Set(choix).size !== 4) continue;
        // Une fois sur deux, l'amorce nue ; sinon, la même question posée sur un prix (le geste en situation).
        const enContexte = randInt(0, 1) === 1;
        const stem = enContexte
            ? `<p>Un article coûte ${it.V} €. Son prix ${it.hausse ? 'augmente' : 'diminue'} de ${it.p} %.</p><p><b>Le nouveau prix, c'est ${it.V} × …</b></p>`
            : `<p><b>${it.hausse ? 'Augmenter' : 'Diminuer'} de ${it.p} %, c'est multiplier par…</b></p>`;
        const payload = makeQcm(choix, bon, stem, opts(L_COEF));
        payload.contenu = { ...payload.contenu, sousType: it.hausse ? 'hausse' : 'baisse', explications: EXPLICATIONS };
        payload._meta.params = { ...it, enContexte, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = { V: 200, p: 20, hausse: true, coef: 120, nv: 240 };
    const payload = makeQcm(['1,2', '0,2', '0,8', '120'], '1,2', `<p><b>Augmenter de 20 %, c'est multiplier par…</b></p>`, opts(L_COEF));
    payload.contenu = { ...payload.contenu, sousType: 'hausse', explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'coefficient_sans_le_1' }, { code: 'coefficient_signe' }, { code: 'coefficient_additif' }] };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirer({ exclure: items.map((it) => `${it.V}|${it.p}|${it.hausse}`) }));
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            const coefLigne = i % 2 === 0;
            if (coefLigne) {
                if (verites[i]) return { ligne: `${it.hausse ? 'Augmenter' : 'Diminuer'} de ${it.p} %, c'est multiplier par ${fmt(it.coef)}.`, code: null };
                const cands = candidatsCoef(it);
                if (!cands.length) return null;
                const x = randomChoice(cands);
                return { ligne: `${it.hausse ? 'Augmenter' : 'Diminuer'} de ${it.p} %, c'est multiplier par ${fmt(x.v)}.`, code: x.code };
            }
            if (verites[i]) return { ligne: `${it.V} € ${verbe(it.hausse)} de ${it.p} %, c'est ${it.nv} €.`, code: null };
            const cands = candidatsValeur(it);
            if (!cands.length) return null;
            const x = randomChoice(cands);
            return { ligne: `${it.V} € ${verbe(it.hausse)} de ${it.p} %, c'est ${x.v} €.`, code: x.code };
        });
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const lignes = ['Augmenter de 20 %, c\'est multiplier par 1,2.', '80 € diminués de 25 %, c\'est 55 €.', 'Diminuer de 50 %, c\'est multiplier par 0,5.', '200 € augmentés de 30 %, c\'est 230 €.'];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], verites: [true, false, true, false], distracteurs: [{ code: 'pourcentage_recopie' }, { code: 'pourcentage_recopie' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'p dans {5, 10, 20, 25, 30, 50}, V multiple de 20 ≤ 1 000, hausse ou baisse à parts égales ; centièmes entiers, deux chemins (throw)',
        'sacrifié : 150 € + 20 % en valeur ; le coefficient 1,2 / 0,8 n\'est pas sacrifié (fait à connaître)',
    ],
    valeur_qcm_unique: 'pourcentage_recopie (V ± p, la principale), pourcentage_seul (la variation), sens_inverse, mauvais_diviseur (V ÷ p)',
    coefficient_qcm_unique: 'coefficient_sans_le_1 (0,2, la principale), coefficient_signe (0,8 pour + 20 %), coefficient_accole (1,5 pour + 5 %), coefficient_additif (120)',
    vf_tableau_vf: 'quatre lignes, deux du coefficient et deux de la valeur, une à trois vraies',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    valeur_qcm_unique: genValeur,
    coefficient_qcm_unique: genCoef,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'valeur', label: L_VALEUR, formatsList: [{ key: 'valeur_qcm_unique', label: 'QCM' }] },
    { id: 'coefficient', label: L_COEF, formatsList: [{ key: 'coefficient_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
